// BloomTracker — 6-level cognitive progression bar (Bloom's taxonomy)
//
// Visualises the student's highest reached cognitive level across all
// bloom updates received during the session. Each level is a labeled
// segment: green (reached), yellow (active), gray (unexplored).

import { useTranslation } from 'react-i18next';
import type { BloomUpdate } from '@hooks/useAnnaChat';

interface BloomTrackerProps {
  bloomUpdates: BloomUpdate[];
  className?: string;
}

const BLOOM_LEVELS = [
  'recognize',
  'understand',
  'apply',
  'analyze',
  'evaluate',
  'create',
] as const;

function getHighestLevel(updates: BloomUpdate[]): number {
  if (updates.length === 0) return -1;
  let highest = -1;
  for (const update of updates) {
    const idx = BLOOM_LEVELS.indexOf(
      update.level as (typeof BLOOM_LEVELS)[number],
    );
    if (idx > highest) highest = idx;
  }
  return highest;
}

export default function BloomTracker({
  bloomUpdates,
  className = '',
}: BloomTrackerProps) {
  const { t } = useTranslation('session');
  const highestIdx = getHighestLevel(bloomUpdates);

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {BLOOM_LEVELS.map((level, idx) => {
        let color: string;
        if (idx < highestIdx) {
          color = 'bg-emerald-500/80 text-white';
        } else if (idx === highestIdx) {
          color = 'bg-yellow-500/80 text-gray-900';
        } else {
          color = 'bg-gray-700/50 text-gray-400';
        }

        return (
          <span
            key={level}
            className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors duration-300 ${color}`}
          >
            {t(`bloomTracker.levels.${level}`)}
          </span>
        );
      })}
    </div>
  );
}
